import React, { useState } from 'react';

const ProductionComponentForm = ({ initialData, onSave, onCancel }) => {
    const [component, setComponent] = useState(initialData || {
        name: '',
        label: '',
        description: '',
        allowsMultiple: true,
        fields: [],
        sortOrder: 0
    });
    const [showFieldForm, setShowFieldForm] = useState(false);
    const [editingFieldIndex, setEditingFieldIndex] = useState(null);
    const [currentField, setCurrentField] = useState({
        name: '',
        label: '',
        type: 'TEXT',
        required: false,
        unit: '',
        placeholder: '',
        defaultValue: '',
        options: [],
        rawMaterialCategorySlug: '',
        calculationFormula: '',
        dependsOn: []
    });

    const fieldTypes = [
        { value: 'TEXT', label: 'Text' },
        { value: 'TEXTAREA', label: 'Text Area' },
        { value: 'NUMBER', label: 'Number' },
        { value: 'SELECT', label: 'Dropdown Select' },
        { value: 'MULTISELECT', label: 'Multi-Select' },
        { value: 'MATERIAL_SELECT', label: 'Material Selector' },
        { value: 'BOOLEAN', label: 'Boolean (Yes/No)' }
    ];

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setComponent(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const addField = () => {
        if (!currentField.name || !currentField.label) {
            alert('Field name and label are required');
            return;
        }

        if (editingFieldIndex !== null) {
            const updatedFields = [...component.fields];
            updatedFields[editingFieldIndex] = currentField;
            setComponent(prev => ({ ...prev, fields: updatedFields }));
            setEditingFieldIndex(null);
        } else {
            setComponent(prev => ({
                ...prev,
                fields: [...prev.fields, currentField]
            }));
        }

        resetFieldForm();
        setShowFieldForm(false);
    };

    const editField = (index) => {
        setCurrentField(component.fields[index]);
        setEditingFieldIndex(index);
        setShowFieldForm(true);
    };

    const removeField = (index) => {
        if (window.confirm('Remove this field?')) {
            const updatedFields = component.fields.filter((_, i) => i !== index);
            setComponent(prev => ({ ...prev, fields: updatedFields }));
        }
    };

    const resetFieldForm = () => {
        setCurrentField({
            name: '',
            label: '',
            type: 'TEXT',
            required: false,
            unit: '',
            placeholder: '',
            defaultValue: '',
            options: [],
            rawMaterialCategorySlug: '',
            calculationFormula: '',
            dependsOn: []
        });
    };

    const handleFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCurrentField(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleOptionChange = (index, field, value) => {
        const newOptions = [...currentField.options];
        newOptions[index] = { ...newOptions[index], [field]: value };
        setCurrentField(prev => ({ ...prev, options: newOptions }));
    };

    const addOption = () => {
        setCurrentField(prev => ({
            ...prev,
            options: [...prev.options, { value: '', label: '' }]
        }));
    };

    const removeOption = (index) => {
        const newOptions = currentField.options.filter((_, i) => i !== index);
        setCurrentField(prev => ({ ...prev, options: newOptions }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!component.name || !component.label) {
            alert('Component name and label are required');
            return;
        }

        if (component.fields.length === 0) {
            alert('Please add at least one field to the component');
            return;
        }

        onSave(component);
    };

    return (
        <div className="card mb-4 border-primary">
            <div className="card-header bg-primary text-white">
                <h6 className="mb-0">
                    {initialData ? 'Edit Production Component' : 'Add Production Component'}
                </h6>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Component Name (unique identifier) *</label>
                            <input
                                type="text"
                                className="form-control"
                                name="name"
                                value={component.name}
                                onChange={handleInputChange}
                                placeholder="e.g., fabrics, threads, zari"
                                disabled={!!initialData}
                                required
                            />
                            <small className="text-muted">Used as identifier in code (no spaces)</small>
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Display Label *</label>
                            <input
                                type="text"
                                className="form-control"
                                name="label"
                                value={component.label}
                                onChange={handleInputChange}
                                placeholder="e.g., Fabrics, Threads, Zari"
                                required
                            />
                        </div>

                        <div className="col-12 mb-3">
                            <label className="form-label">Description</label>
                            <textarea
                                className="form-control"
                                name="description"
                                value={component.description || ''}
                                onChange={handleInputChange}
                                rows="2"
                                placeholder="Describe what this component represents..."
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    name="allowsMultiple"
                                    checked={component.allowsMultiple}
                                    onChange={handleInputChange}
                                    id="allowsMultiple"
                                />
                                <label className="form-check-label" htmlFor="allowsMultiple">
                                    Allow multiple items per colorway
                                </label>
                            </div>
                            <small className="text-muted">If checked, users can add multiple entries of this component (e.g., multiple fabrics)</small>
                        </div>
                    </div>

                    {/* Fields Section */}
                    <div className="mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <label className="fw-bold">Component Fields</label>
                            {!showFieldForm && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => {
                                        resetFieldForm();
                                        setShowFieldForm(true);
                                    }}
                                >
                                    + Add Field
                                </button>
                            )}
                        </div>

                        {/* Field Form */}
                        {showFieldForm && (
                            <div className="card mb-3 bg-light">
                                <div className="card-body">
                                    <h6>{editingFieldIndex !== null ? 'Edit Field' : 'Add Field'}</h6>
                                    <div className="row">
                                        <div className="col-md-6 mb-2">
                                            <label className="form-label small">Field Name *</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                name="name"
                                                value={currentField.name}
                                                onChange={handleFieldChange}
                                                placeholder="e.g., fabricType"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-2">
                                            <label className="form-label small">Display Label *</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                name="label"
                                                value={currentField.label}
                                                onChange={handleFieldChange}
                                                placeholder="e.g., Fabric Type"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <label className="form-label small">Field Type</label>
                                            <select
                                                className="form-select form-select-sm"
                                                name="type"
                                                value={currentField.type}
                                                onChange={handleFieldChange}
                                            >
                                                {fieldTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <label className="form-label small">Unit (Optional)</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                name="unit"
                                                value={currentField.unit}
                                                onChange={handleFieldChange}
                                                placeholder="e.g., meters, kg"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-2">
                                            <div className="form-check mt-4">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    name="required"
                                                    checked={currentField.required}
                                                    onChange={handleFieldChange}
                                                    id="fieldRequired"
                                                />
                                                <label className="form-check-label small" htmlFor="fieldRequired">
                                                    Required Field
                                                </label>
                                            </div>
                                        </div>

                                        {/* For MATERIAL_SELECT type */}
                                        {currentField.type === 'MATERIAL_SELECT' && (
                                            <div className="col-md-12 mb-2">
                                                <label className="form-label small">Raw Material Category Slug</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    name="rawMaterialCategorySlug"
                                                    value={currentField.rawMaterialCategorySlug}
                                                    onChange={handleFieldChange}
                                                    placeholder="e.g., fabric, thread"
                                                />
                                                <small className="text-muted">Which raw material category to fetch from</small>
                                            </div>
                                        )}

                                        {/* For SELECT/MULTISELECT types */}
                                        {(currentField.type === 'SELECT' || currentField.type === 'MULTISELECT') && (
                                            <div className="col-12 mb-2">
                                                <label className="form-label small">Options</label>
                                                <div className="border rounded p-2 bg-white">
                                                    {currentField.options.map((opt, idx) => (
                                                        <div key={idx} className="row mb-2">
                                                            <div className="col-md-5">
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Value"
                                                                    value={opt.value}
                                                                    onChange={(e) => handleOptionChange(idx, 'value', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="col-md-5">
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    placeholder="Label"
                                                                    value={opt.label}
                                                                    onChange={(e) => handleOptionChange(idx, 'label', e.target.value)}
                                                                />
                                                            </div>
                                                            <div className="col-md-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => removeOption(idx)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={addOption}
                                                    >
                                                        + Add Option
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        <div className="col-12 mt-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-success me-2"
                                                onClick={addField}
                                            >
                                                {editingFieldIndex !== null ? 'Update' : 'Add'} Field
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => {
                                                    setShowFieldForm(false);
                                                    setEditingFieldIndex(null);
                                                    resetFieldForm();
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Fields List */}
                        {component.fields.length > 0 && (
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead className="table-secondary">
                                        <tr>
                                            <th>Name</th>
                                            <th>Label</th>
                                            <th>Type</th>
                                            <th>Required</th>
                                            <th>Unit</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {component.fields.map((field, idx) => (
                                            <tr key={idx}>
                                                <td>{field.name}</td>
                                                <td>{field.label}</td>
                                                <td>{field.type}</td>
                                                <td>{field.required ? 'Yes' : 'No'}</td>
                                                <td>{field.unit || '-'}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-warning me-1"
                                                        onClick={() => editField(idx)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => removeField(idx)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 d-flex justify-content-end">
                        <button type="button" className="btn btn-secondary me-2" onClick={onCancel}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary">
                            {initialData ? 'Update Component' : 'Add Component'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductionComponentForm;